import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MapPin, X, Check, Truck } from "lucide-react";

const STORAGE = "arre_coverage";

export default function ZipCodePopup() {
  const [visible, setVisible] = useState(false);
  const [zip, setZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE);
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    const handler = () => {
      try {
        const s = localStorage.getItem(STORAGE);
        if (s) {
          const parsed = JSON.parse(s);
          setZip(parsed.zip);
          setResult(parsed);
        }
      } catch {}
      setVisible(true);
    };
    window.addEventListener("open_zip_popup", handler);
    return () => window.removeEventListener("open_zip_popup", handler);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{5}$/.test(zip)) {
      setError("Ingresa un CP válido de 5 dígitos, compare.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.get(`/coverage/check?zip=${zip}`);
      setResult(data);
      if (data.covered) {
        localStorage.setItem(STORAGE, JSON.stringify(data));
        window.dispatchEvent(new Event("arre_coverage_updated"));
      }
    } catch {
      setError("Algo falló. Inténtalo otra vez.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setVisible(false);
    // Save a "dismissed" marker so it doesn't keep popping up
    if (!localStorage.getItem(STORAGE)) {
      localStorage.setItem(STORAGE, JSON.stringify({ dismissed: true, covered: false, zip: "" }));
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="zip-popup">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={close} />
      <div className="relative w-full max-w-xl bg-arre-surface border border-arre-border shadow-2xl grain animate-fade-up">
        <button
          onClick={close}
          className="absolute top-4 right-4 text-white/60 hover:text-white z-10"
          aria-label="Cerrar"
          data-testid="zip-close-btn"
        >
          <X className="w-5 h-5" />
        </button>
        {/* Image strip */}
        <div
          className="h-32 bg-cover bg-center relative"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1625604086988-6e41981275fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=940')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-arre-surface/20 to-arre-surface" />
        </div>

        <div className="p-8 md:p-10 -mt-8 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-arre-red/15 border border-arre-red/40 text-arre-red text-[10px] tracking-[0.3em] uppercase mb-4">
            <MapPin className="w-3 h-3" strokeWidth={2} />
            Cobertura
          </div>
          <h2 className="font-display text-5xl md:text-6xl tracking-wider text-white uppercase mb-2 leading-none">
            ¿Qué onda,<br /><span className="text-arre-red">compare?</span>
          </h2>
          <p className="text-white/70 text-sm font-sans mb-6 max-w-md">
            Ingresa tu código postal y te decimos si alcanzamos a llevarte la mejor carne 100% sonorense a tu puerta.
          </p>

          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              inputMode="numeric"
              maxLength={5}
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
              placeholder="Ej. 06600"
              className="flex-1 bg-arre-black border border-arre-border px-4 py-4 text-white font-display text-2xl tracking-[0.3em] focus:outline-none focus:border-arre-red"
              data-testid="zip-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="btn-arre bg-arre-red hover:bg-arre-red-dark text-white font-display text-xl tracking-widest uppercase px-8 py-4 disabled:opacity-60"
              data-testid="zip-submit-btn"
            >
              {loading ? "Buscando..." : "Verificar"}
            </button>
          </form>

          {error && (
            <p className="text-arre-red text-sm mt-3" data-testid="zip-error">
              {error}
            </p>
          )}

          {result && (
            <div
              className={`mt-6 p-5 border ${
                result.covered ? "border-green-600/40 bg-green-900/10" : "border-arre-red/40 bg-arre-red/10"
              }`}
              data-testid="zip-result"
            >
              <div className="flex items-start gap-3">
                {result.covered ? (
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <X className="w-5 h-5 text-arre-red mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className="text-white font-semibold text-base">{result.message}</p>
                  {result.covered && (
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-2 text-white/70">
                        <Truck className="w-4 h-4" />
                        <span>
                          Envío: <span className="text-white font-bold">${result.shipping_fee} MXN</span>
                        </span>
                      </div>
                      {result.same_day && (
                        <div className="px-2 py-1 bg-arre-red text-white text-[10px] tracking-widest uppercase font-bold">
                          Hoy mismo
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {result.covered && (
                <button
                  onClick={close}
                  className="mt-4 w-full btn-arre bg-white text-arre-black font-display text-lg tracking-widest uppercase px-6 py-3 hover:bg-arre-cream"
                  data-testid="zip-continue-btn"
                >
                  Seguir al catálogo →
                </button>
              )}
            </div>
          )}

          <p className="text-white/40 text-xs mt-6 text-center">
            CDMX y EDOMEX: entrega el mismo día si pides antes de las 12:00 PM
          </p>
        </div>
      </div>
    </div>
  );
}
